<template>
  <div class="title-helper">
    <app-form
      ref="form_ref"
      :submit-request="onSearch"
      :is-show-button="false"
      :form-props="{ inline: true }"
      :form-data="helper_config.filter_config"
      @hook:mounted="emitConfirmEvent"
    />
    <div v-loading="helper_config.loading" class="title-list">
      <el-scrollbar class="h-full" wrap-style="overflow-x: hidden;">
        <div
          v-for="(availableOption, index) in (helper_config.response ? helper_config.response.list : [])"
          :key="index"
          class="flex items-center justify-between title-item"
          :class="{
            'is-selected': hasSelectedOption(availableOption),
            'is-disabled': selected_value.length >= multipleLimit && !hasSelectedOption(availableOption),
          }"
          @click="toggleOption(availableOption)"
        >
          <div class="flex items-center">
            <span class="title-text">{{ availableOption.text }}</span>
            <el-tag v-if="availableOption.tag" size="mini">{{ availableOption.tag }}</el-tag>
          </div>
          <i
            v-show="hasSelectedOption(availableOption)"
            class="el-icon-success"
          />
        </div>
        <el-empty v-show="helper_config.response.list.length === 0" />
      </el-scrollbar>
    </div>
  </div>
</template>

<script>
import { getCreativeTextApi } from '@/api/tool/creativeText';
import { CHANNEL_INTERFACE_TYPE } from '@/enums';
import { isFunction } from '@/utils/is';

const serialize = (sources) => JSON.stringify(sources);
const deepClone = (sources) => JSON.parse(JSON.stringify(sources));

export default {
  components: {
    'app-form': () => import('@/components/app-form'),
  },
  props: {
    pageConfig: {
      type: Object,
      required: true,
    },
    initOps: {
      type: Object,
      required: true,
    },
    multipleLimit: {
      type: Number,
      default: 200,
    },
  },
  data() {
    return {
      helper_config: {
        loading: false,
        filter_config: require('./model/filter-config.js')(this),
        response: {
          list: [],
        },
      },
      selected_value: [],
    };
  },
  methods: {
    /**
     * 文案类型的值进行排序后重新赋值
     * @param {Array<number>} val 文案类型选中的值
     * @returns {void}
     */
    dispatchFilteringSort(val) {
      const { text_types } = this.helper_config.filter_config;
      text_types.value = val.sort((first, last) => first - last);
    },
    /**
     * 派发改变事件
     * @returns {void}
     */
    async dispatchChangeEvent() {
      const ret = await this.getAssignSelection();
      this.$emit('change', ret);
    },
    /**
     * 已选列表中是否存在指定选项
     * @param {Object} selected_option 指定选项对象
     * @returns {boolean}
     */
    hasSelectedOption(selected_option) {
      const is_equal = (option) => serialize(option) === serialize(selected_option);
      return this.selected_value.some(is_equal);
    },
    /**
     * 获取选项在选项列表中所在的索引位置
     * @param {Array} options 选项列表
     * @param {Object} option 选项对象
     */
    getOptionIndex(options, option) {
      return options.findIndex((item) => serialize(item) === serialize(option));
    },
    /**
     * 添加选项到已选列表
     * @param {Object} selected_option 选项
     * @returns {void}
     */
    addSelectedOption(selected_option) {
      this.selected_value.push(selected_option);
      this.dispatchChangeEvent();
    },
    /**
     * 从已选列表移除选项
     * @param {Object} selected_option 选项对象
     * @returns {void}
     */
    removeSelectedOption(selected_option) {
      const index = this.getOptionIndex(this.selected_value, selected_option);
      if (index !== -1) {
        this.selected_value.splice(index, 1);
        this.dispatchChangeEvent();
      }
    },
    /**
     * 切换选项选中状态
     * @param {Object} option 选项对象
     * @returns {void}
     */
    toggleOption(option) {
      const isExistOption = this.hasSelectedOption(option);
      const isOverflow = this.selected_value.length >= this.multipleLimit;
      if (isOverflow && !isExistOption) {
        return;
      }
      isExistOption
        ? this.removeSelectedOption(option)
        : this.addSelectedOption(option);
    },
    /**
     * 触发表单确认事件
     * @returns {void}
     */
    emitConfirmEvent() {
      const form_ref = this.$refs.form_ref;
      if (!form_ref || !isFunction(form_ref.confirm)) {
        return;
      }
      form_ref.confirm();
    },
    /**
     * 搜索列表数据
     * @param {Object} params 查询数据
     */
    async onSearch(params) {
      try {
        const {
          channel_interface,
          advertiser_id,
          max_text_length,
          keyword,
          category_first_level,
          category_second_level,
          number,
          text_types,
        } = params;
        const filtering = {
          max_text_length,
          ...(keyword && { keyword }),
        };
        const base_params = {
          channel_interface,
          advertiser_id,
          filtering,
        };
        const channel_params = {
          [CHANNEL_INTERFACE_TYPE.GDT]: {
            gdt_params: {
              filtering: {
                category_first_level,
                number,
                ...(category_second_level && { category_second_level }),
                ...(text_types.length && { text_types }),
              },
            },
          },
        };
        const payload = {
          ...base_params,
          ...(channel_params[channel_interface] || {}),
        };
        this.helper_config.loading = true;
        this.selected_value.splice(0);
        const response = await this.getTitleHelperList(payload);
        Object.assign(this.helper_config, { response });
      } finally {
        this.helper_config.loading = false;
      }
    },
    /**
     * 获取推荐标题列表
     * @param {Object} params 查询参数
     */
    async getTitleHelperList(params) {
      try {
        const response = await getCreativeTextApi(params);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(new Error(error));
      }
    },
    /**
     * 获取分配数据
     * @returns {Array} 标题对象数组或空数组
     */
    async getAssignSelection() {
      const ret = this.selected_value.map(({ text: name }) => {
        return {
          advertiser: this.initOps.channel_interface,
          name,
        };
      });
      return deepClone(ret);
    },
  },
};
</script>

<style lang="scss">
.title-helper {
  .title-list {
    height: 400px;
    .title-item {
      padding: 0 16px;
      box-sizing: border-box;
      line-height: 40px;
      font-size: 14px;
      cursor: pointer;
      color: #606266;
      &:hover {
        background-color: #f5f7fa;
      }
      &.is-disabled {
        color: #c0c4cc;
        cursor: not-allowed;
      }
      &.is-selected {
        font-weight: 700;
        background-color: #f5f7fa;
        > i {
          font-size: 20px;
          color: #409eff;
        }
      }
      .title-text {
        font-size: 14px;
        margin-right: 8px;
      }
    }
  }
  .hidden {
    display: none!important;
  }
}
</style>
